import React, { useEffect, useState } from 'react'
import { Typography, Grid, Box, TextField } from '@material-ui/core'
import { Controller, useForm } from 'react-hook-form'
import { Alert, Autocomplete, Skeleton } from '@material-ui/lab'
import { isEmpty } from 'lodash'

import { Label, Select } from 'components'
import { DocumentCodeTable } from './components'
import useFetch from 'hooks/useFetch'
import useDocumentGenerate from 'hooks/useDocumentGenerate'
import useSnackbar from 'hooks/useSnackbar'

import * as service from 'service'
import helpers from 'helpers'
import useStyles from './styles'

const SelectDocumentTemplateStep = () => {
  const [isLoadingDocumentTemplate, setIsLoadingDocumentTemplate] =
    useState(false)

  const {
    documentCategories,
    setDisabledNextButton,
    documentTemplate,
    setDocumentTemplate,
    wantsToAdvanceStep,
    handleNext,
    setDocumentVariables,
  } = useDocumentGenerate()

  const { snackbar } = useSnackbar()
  const classes = useStyles()

  const { control, watch } = useForm({
    defaultValues: {
      documentCategoryId: documentTemplate?.documentCategory?.id || '',
      documentTemplate: documentTemplate || null,
    },
  })

  const documentCategoryIdSelected = watch('documentCategoryId')
  const documentTemplateSelected = watch('documentTemplate')

  const {
    response: responseDocumentTemplates,
    isLoading: isLoadingDocumentTemplates,
  } = useFetch(
    service.dponet.documentTemplates.get,
    { documentCategoryId: documentCategoryIdSelected, perPage: 99999 },
    [documentCategoryIdSelected],
    !!documentCategoryIdSelected,
  )

  const documentTemplates =
    responseDocumentTemplates?.data?.documentTemplates || []

  const getDocumentTemplateData = async (documentTemplateId) => {
    try {
      setIsLoadingDocumentTemplate(true)

      const [documentTemplateResponse, documentVariablesResponse] =
        await Promise.all([
          service.dponet.documentTemplates.get({ documentTemplateId }),
          service.dponet.documentTemplateVariables.get({ documentTemplateId }),
        ])

      setDocumentTemplate(
        documentTemplateResponse?.data?.documentTemplate || null,
      )
      setDocumentVariables(
        documentVariablesResponse?.data?.documentVariables || [],
      )
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.definedErrorMessage(error),
        variant: 'error',
      })
    } finally {
      setIsLoadingDocumentTemplate(false)
    }
  }

  useEffect(() => {
    if (documentCategoryIdSelected !== documentTemplate?.documentCategory?.id) {
      setDocumentTemplate(null)
    }
  }, [documentCategoryIdSelected])

  useEffect(() => {
    setDisabledNextButton(
      isEmpty(documentTemplate) && !isLoadingDocumentTemplate,
    )
  }, [documentTemplate])

  useEffect(() => {
    if (!!documentTemplateSelected?.id) {
      getDocumentTemplateData(documentTemplateSelected?.id)
    }
  }, [documentTemplateSelected?.id])

  useEffect(() => {
    if (wantsToAdvanceStep) {
      handleNext()
    }
  }, [wantsToAdvanceStep])

  return (
    <>
      <form>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6" color="secondary">
              Selecione o modelo de documento a partir da lista de documentos
              disponíveis
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Label
              title="Categoria de documentos"
              description="Selecione a categoria do documento que deseja criar."
              paddingBoxTitle={0}
            >
              <Controller
                as={
                  <Select
                    fullWidth
                    variant="outlined"
                    items={documentCategories || []}
                  />
                }
                control={control}
                name="documentCategoryId"
                mode="onBlur"
              />
            </Label>
          </Grid>

          {watch('documentCategoryId') && (
            <Grid item xs={12}>
              {isLoadingDocumentTemplates ? (
                <Box>
                  <Skeleton variant="text" width="30%" component="h4" />
                  <Skeleton variant="text" width="50%" component="p" />
                  <Skeleton variant="text" width="100%" height={55} />
                </Box>
              ) : (
                <>
                  {documentTemplates.length === 0 ? (
                    <Alert severity="info">
                      <Typography>
                        Nenhum modelo de documento encontrado nesta categoria.
                      </Typography>
                    </Alert>
                  ) : (
                    <Label
                      title="Modelo de documento"
                      description="Selecione o modelo do documento que deseja criar."
                      paddingBoxTitle={0}
                    >
                      <Controller
                        as={
                          <Autocomplete
                            options={documentTemplates}
                            getOptionLabel={(option) => option.name || ''}
                            getOptionSelected={(option, value) => {
                              return option.id === value?.id
                            }}
                            renderInput={(params) => (
                              <TextField {...params} variant="outlined" />
                            )}
                          />
                        }
                        control={control}
                        name="documentTemplate"
                        onChange={([, data]) => data}
                        mode="onChange"
                      />
                    </Label>
                  )}
                </>
              )}
            </Grid>
          )}

          {documentTemplateSelected?.documentCategory?.id ===
            documentCategoryIdSelected && (
            <Grid item xs={12}>
              {isLoadingDocumentTemplate ? (
                <Box>
                  <Skeleton variant="text" width="30%" component="h4" />
                  <Skeleton variant="text" width="100%" component="p" />
                </Box>
              ) : (
                <>
                  <DocumentCodeTable code={documentTemplateSelected?.code} />
                </>
              )}
            </Grid>
          )}
        </Grid>
      </form>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {documentTemplateSelected && (
            <Box className={classes.backgroundColor}>
              <Box mb={1}>
                <Typography variant="body">
                  <strong>Descrição</strong>
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                {documentTemplate?.description ||
                  'Descrição não disponível para este modelo.'}
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>
    </>
  )
}

export default SelectDocumentTemplateStep
